import { useState, useRef, useEffect, useReducer } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { FaInfoCircle } from 'react-icons/fa';

import Accordion from '../components/Accordion';
import Button from '../components/inputs/Button';
import Plans from '../components/Plans';
import UserProfilePaymentMethods from '../components/payments/UserProfilePaymentMethods';
import UserProfileDetails from '../components/UserProfileDetails';
import { useAuth } from '../hooks/useAuth';
import { reducer, initialState, START_ACTION } from '../reducers/actionReducer';
import ActionModal from '../components/ActionModal';
import DeleteAccount from '../components/actions/DeleteAccount';
import { USER_DETAILS_QUERY, CUSTOMER_QUERY } from '../queries';

function getCircleCircumference(circleRef) {
  if (circleRef.current) {
    return circleRef.current.r.baseVal.value * 2 * Math.PI;
  }
  return 80 * 2 * Math.PI;
}

function calculateProfileProgress(weightedAttrs) {
  let totalComplete = 0;
  let totalWeight = 0;

  weightedAttrs.forEach(({ isCompleted, weight }) => {
    totalWeight += weight;

    if (isCompleted) {
      totalComplete += weight;
    }
  });

  const result = Math.round(
    Number.parseFloat((totalComplete / totalWeight) * 100)
  );

  return result;
}

function ProfileProgress() {
  const { data } = useQuery(USER_DETAILS_QUERY);
  const userDetails = data?.userDetails;

  const { data: dataCustomer } = useQuery(CUSTOMER_QUERY);
  const paymentMethods = dataCustomer?.customer?.paymentMethods;
  const hasPaymentMethod = paymentMethods?.length > 0;

  const [progress, setProgress] = useState(0);
  const circleRef = useRef();
  const circumference = getCircleCircumference(circleRef);
  const offset = circumference - (progress / 100) * circumference;

  useEffect(() => {
    if (userDetails) {
      const { __typename, email, ...countableAttrs } = userDetails;

      const weightedAttrs = [];

      Object.entries(countableAttrs).forEach(([key, value]) =>
        weightedAttrs.push({
          name: key,
          isCompleted: !!value,
          weight: 1,
        })
      );

      weightedAttrs.push({
        name: 'Payment Method',
        isCompleted: !!hasPaymentMethod,
        weight: 2,
      });

      setProgress(calculateProfileProgress(weightedAttrs));
    }
  }, [userDetails, hasPaymentMethod]);

  return (
    <>
      <h4>Profile Strength</h4>
      <div className="relative flex flex-col items-center mb-6">
        <div className="absolute inset-0 h-full w-full flex justify-center items-center">
          <div className="font-light text-3xl ">{`${progress}%`}</div>
        </div>
        <svg
          className="relative z-20 stroke-current text-primary"
          height="200"
          width="200"
        >
          <circle
            style={{
              strokeDasharray: `${circumference} ${circumference}`,
              strokeDashoffset: offset,
              transition: 'stroke-dashoffset 1s',
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%',
            }}
            fill="transparent"
            strokeWidth="10"
            r="80"
            cx="100"
            cy="100"
            ref={circleRef}
          />
        </svg>
        <svg
          className="absolute z-10 stroke-current text-gray-200"
          height="200"
          width="200"
        >
          <circle
            fill="transparent"
            strokeWidth="10"
            r="80"
            cx="100"
            cy="100"
          />
        </svg>
      </div>
      <div className="text-sm w-40 2xl:w-full flex justify-center text-center">
        {Math.round(progress) === 100
          ? 'Thanks for completing your profile!'
          : 'Complete your profile to speed up checkout'}
      </div>
      {!hasPaymentMethod && (
        <div className="w-40 2xl:w-full border rounded-md px-3 py-2 text-sm text-grey-400 shadow mt-8 text-sm text-gray-600">
          <div className="flex items-center">
            <div className="self-start">
              <FaInfoCircle className="inline w-4 h-4 mr-2" />
            </div>{' '}
            Add a payment method to{' '}
            {progress === 80
              ? 'to complete your profile'
              : 'increase profile strength (+20%)'}
          </div>
        </div>
      )}
      <Link className="mt-36" to="../../mywork">
        <Button primary>View Activity</Button>
      </Link>
    </>
  );
}

export default function UserProfile() {
  const location = useLocation();
  const { user } = useAuth();

  const [openAccordion, setOpenAccordion] = useState(() => {
    if (location.search) {
      const query = new URL(window.location).search;
      const urlParams = new URLSearchParams(query);
      if (urlParams.get('addPaymentMethod')) {
        return 'Payment';
      }
      if (urlParams.get('plan')) {
        return 'Plan';
      }
    }

    return null;
  });

  const [actionState, dispatchAction] = useReducer(reducer, initialState);

  return (
    <div className="flex h-full w-full p-2">
      <div className="relative flex flex-grow flex-shrink h-full">
        <div className="absolute inset-0 flex flex-col overflow-auto">
          <Accordion
            className=""
            label="Profile"
            open={openAccordion === 'Profile'}
            handleClick={() =>
              setOpenAccordion((prevState) =>
                prevState === 'Profile' ? null : 'Profile'
              )
            }
          >
            <UserProfileDetails />
          </Accordion>
          <Accordion
            className=""
            label="Payment"
            open={false}
            handleClick={() => {}}
            disabled={true}
          >
            <UserProfilePaymentMethods />
          </Accordion>
          <Accordion
            className="mb-2"
            label="Plan"
            open={false}
            handleClick={() => {}}
            disabled={true}
          >
            <div className="flex flex-col justify-between flex-grow h-screen">
              <Plans currentPlan={user.plan} />
              <div className="flex flex-row pt-48">
                <Button
                  className="border-background-dark hover:bg-background-dark hover:text-white"
                  small
                  short
                  onClick={() =>
                    dispatchAction({
                      type: START_ACTION,
                      payload: {
                        name: 'Delete Audience',
                        component: (
                          <DeleteAccount dispatchAction={dispatchAction} />
                        ),
                        continueLabel: 'Permanently delete account',
                        continueButtonModifier: 'danger',
                      },
                    })
                  }
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </Accordion>
        </div>
      </div>
      <div className="flex flex-col px-6 pt-12 items-center border-l">
        <ProfileProgress />
      </div>
      {actionState.inProgress && (
        <ActionModal
          actionState={actionState}
          dispatchAction={dispatchAction}
          show={actionState.inProgress}
        />
      )}
    </div>
  );
}
